import { useMemo, useState } from 'react';
import { Palette, ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import { extractSvgColors, replaceSvgColor } from '../../lib/svgColorUtils';
import type { SvgElement, CanvasElement } from '../../types';

interface SvgColorEditorProps {
  element: SvgElement;
  onUpdateElement: (id: string, updates: Partial<CanvasElement>) => void;
}

export default function SvgColorEditor({ element, onUpdateElement }: SvgColorEditorProps) {
  const [editingColorHex, setEditingColorHex] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  const colors = useMemo(() => {
    return extractSvgColors(element.svgMarkup);
  }, [element.svgMarkup]);

  const handleColorChange = (oldHex: string, newHex: string) => {
    const newMarkup = replaceSvgColor(element.svgMarkup, oldHex, newHex);
    onUpdateElement(element.id, { svgMarkup: newMarkup } as Partial<CanvasElement>);
  };

  if (colors.length === 0) {
    return (
      <div className="py-2">
        <p className="text-[10px] text-white/30">No editable colors found in this SVG.</p>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-2 hover:bg-white/[0.02] transition-colors rounded"
      >
        {expanded ? <ChevronDown size={13} className="text-white/30" /> : <ChevronRight size={13} className="text-white/30" />}
        <Palette size={13} className="text-cyan-400" />
        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">SVG Colors</span>
        <span className="text-[10px] text-white/25 ml-auto">{colors.length}</span>
      </button>

      {expanded && (
        <div className="space-y-1.5 pt-1">
          {colors.map((color) => (
            <div key={color.hex} className="flex items-center gap-2">
              <button
                onClick={() => setEditingColorHex(editingColorHex === color.hex ? null : color.hex)}
                className="w-6 h-6 rounded border border-white/20 hover:border-white/40 transition-colors flex-shrink-0 relative overflow-hidden"
                style={{ backgroundColor: color.hex }}
                title={`${color.label} -- Click to edit`}
              >
                <div className="absolute inset-0 -z-10" style={{
                  backgroundImage: 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)',
                  backgroundSize: '6px 6px',
                  backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0px'
                }} />
              </button>

              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-white/60 truncate">{color.label}</div>
                <div className="text-[9px] text-white/25 font-mono">{color.hex}</div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {color.attributes.includes('fill') && (
                  <span className="text-[8px] px-1 py-0.5 rounded bg-white/[0.06] text-white/30">fill</span>
                )}
                {color.attributes.includes('stroke') && (
                  <span className="text-[8px] px-1 py-0.5 rounded bg-white/[0.06] text-white/30">stroke</span>
                )}
                <span className="text-[9px] text-white/20">{color.count}x</span>
              </div>

              <button
                className="p-0.5 rounded hover:bg-white/[0.06] text-white/15 hover:text-cyan-400 transition-colors"
                title="Map to brand color (coming soon)"
                onClick={(e) => e.stopPropagation()}
              >
                <Link2 size={11} />
              </button>
            </div>
          ))}

          {editingColorHex && (
            <div className="mt-2 p-2 bg-white/[0.03] rounded-lg border border-white/[0.06]">
              <div className="text-[9px] text-white/30 mb-2">
                Editing: {colors.find((c) => c.hex === editingColorHex)?.label || editingColorHex}
              </div>
              <input
                type="color"
                value={editingColorHex}
                onChange={(e) => {
                  const newColor = e.target.value;
                  handleColorChange(editingColorHex, newColor);
                  setEditingColorHex(newColor);
                }}
                className="w-full h-8 rounded border border-white/10 cursor-pointer bg-transparent"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
